import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'

type UiPrefs = Record<string, unknown>

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value)

const readUiPrefs = (configuracoes: unknown): UiPrefs => {
  if (!isRecord(configuracoes)) return {}
  const ui = configuracoes.ui
  return isRecord(ui) ? ui : {}
}

const storageKey = (key: string) => `ui.${key}`

const readLocal = <T>(key: string): T | undefined => {
  try {
    const raw = window.localStorage.getItem(storageKey(key))
    return raw == null ? undefined : (JSON.parse(raw) as T)
  } catch {
    return undefined
  }
}

const writeLocal = (key: string, value: unknown): void => {
  try {
    window.localStorage.setItem(storageKey(key), JSON.stringify(value))
  } catch {
    /* storage indisponível: fica só o perfil */
  }
}

/**
 * Preferência de interface com a fonte da verdade em
 * `profiles.configuracoes.ui[key]` e o localStorage como cache — o mesmo
 * arranjo de useTourSeen. O cache é lido de forma síncrona na inicialização
 * para o primeiro paint já sair no estado certo; quando o perfil chega (ou
 * muda em outro aparelho) ele vence e atualiza o cache.
 *
 * `T` deve ser um primitivo JSON (boolean/string/number).
 */
export function useUiPreference<T>(key: string, defaultValue: T): [T, (value: T) => void] {
  const { profile, updateProfile } = useAuth()

  const [value, setValueState] = useState<T>(() => readLocal<T>(key) ?? defaultValue)

  const remote = readUiPrefs(profile?.configuracoes)[key] as T | undefined
  // Último valor vindo do perfil que já aplicamos — evita que um perfil ainda
  // antigo (update em voo) desfaça um setValue recém-feito.
  const lastRemoteRef = useRef<T | undefined>(undefined)

  useEffect(() => {
    if (remote !== undefined && remote !== lastRemoteRef.current) {
      lastRemoteRef.current = remote
      setValueState(remote)
      writeLocal(key, remote)
    }
  }, [key, remote])

  const setValue = useCallback(
    (next: T) => {
      setValueState(next)
      writeLocal(key, next)
      lastRemoteRef.current = next
      if (!profile) return
      const current = isRecord(profile.configuracoes) ? profile.configuracoes : {}
      const nextConfig = { ...current, ui: { ...readUiPrefs(current), [key]: next } }
      void updateProfile({ configuracoes: nextConfig } as Parameters<typeof updateProfile>[0]).catch(() => {
        /* cache local já gravado; o perfil tenta de novo na próxima mudança */
      })
    },
    [key, profile, updateProfile]
  )

  return [value, setValue]
}
